/**
 * 军师报错的可诊断化(2026-07-29 测试反馈 bug 3:「生成图显示请求失败,但最后可以生成海报」)。
 *
 * 现象里那四个字来自 `httpClient` 的 `json.message || "请求失败"` —— 说明后端返了
 * `success:false` 且 **message 为空**。而前端 catch 里只取 `e.message`,把 `errorCode`
 * 和 traceId 一并丢了 → 界面上只剩「请求失败」,既看不出哪一步挂了,也没法拿去捞日志。
 *
 * 这个模块**不修**后端那个空 message(真因要拿 traceId 去 backend/ai worker 日志里捞),
 * 它只保证:下一次再出,用户和我们都能立刻知道「是什么错、去哪查」。
 */
import { maskVendor } from "@/shared/lib/vendorMask";
import { ApiError } from "@/shared/api/httpClient";

export interface AdvisorErrorInfo {
  /** 给用户看的一句话。 */
  text: string;
  /** 后端错误码;拿不到时为空串。 */
  errorCode: string;
  /** 本次请求的 trace id(httpClient 每次请求都会生成并回填到 ApiError 上时可用)。 */
  traceId: string;
}

/**
 * 把任意 catch 到的东西整成「说得出是什么错」的结构。
 *
 * 优先级:后端 message → 错误码 → 兜底文案。**errorCode 单独留字段**而不是拼进 text ——
 * 展示层要用它做「复制诊断信息」,拼成一句话就没法单独取了。
 */
export function describeAdvisorError(e: unknown, fallback: string): AdvisorErrorInfo {
  if (e instanceof ApiError) {
    const code = e.errorCode && e.errorCode !== "UNKNOWN" ? e.errorCode : "";
    // 后端 message 为空正是 bug 3 的现场 —— 此时别再显示「请求失败」这种等于没说的话,
    // 把错误码和 HTTP 状态亮出来,至少能定位到是哪一类失败。
    const text = e.message?.trim()
      ? e.message
      : code
        ? `${fallback}(${code})`
        : `${fallback}(HTTP ${e.httpStatus})`;
    return { text: maskVendor(text), errorCode: code, traceId: readTraceId(e) };
  }
  if (e instanceof Error && e.message?.trim()) {
    // 非 ApiError(SSE 流里的 error 事件等)后端原文也可能带上游名,同样脱敏。
    return { text: maskVendor(e.message), errorCode: "", traceId: "" };
  }
  return { text: fallback, errorCode: "", traceId: "" };
}

/** ApiError 上若挂了 traceId 就取出来(httpClient 未来回填时自动生效,现在返空串)。 */
function readTraceId(e: ApiError): string {
  const t = (e as unknown as { traceId?: unknown }).traceId;
  return typeof t === "string" ? t : "";
}

/** 一行可复制的诊断串,给用户「复制」后直接贴给我们查日志。 */
export function advisorDiagnosticLine(info: AdvisorErrorInfo): string {
  return [
    info.text,
    info.errorCode && `code=${info.errorCode}`,
    info.traceId && `trace=${info.traceId}`,
  ]
    .filter(Boolean)
    .join(" · ");
}
